// src/components/common/TableList.jsx
// -----------------------------------------------------------------------------
// Component requirement #1: TableList.jsx
// Displays records in a data grid/table with search, column sorting, pagination,
// loading state, empty state message, and row actions (Edit, Delete, custom actions).
// Includes draggable resizable columns.
// -----------------------------------------------------------------------------
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Loader from './Loader';
import './common.css';

export default function TableList({
  columns = [],
  data = [],
  paginatedData = data,
  loading = false,
  emptyMessage = 'No records found.',
  idKey = 'id',

  // Search & Filter props
  searchTerm = '',
  onSearchChange,

  // Sort props
  sortConfig = { key: null, direction: 'asc' },
  onSort,

  // Pagination props
  currentPage = 1,
  totalPages = 1,
  totalRecords = data.length,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,

  // Actions
  onEdit,
  onDelete,
  renderActions,
  renderHeaderActions,
}) {
  const [colWidths, setColWidths] = useState({});

  const startResize = (e, colKey) => {
    e.preventDefault();
    const isTouch = e.type === 'touchstart';
    const thElement = e.currentTarget.parentElement;
    const startWidth = thElement.offsetWidth;
    const startX = isTouch ? e.touches[0].clientX : e.clientX;

    const onMove = (moveEvent) => {
      const currentX = isTouch ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const deltaX = currentX - startX;
      const newWidth = Math.max(60, startWidth + deltaX); // Min width 60px
      setColWidths((prev) => ({
        ...prev,
        [colKey]: `${newWidth}px`,
      }));
    };

    const onEnd = () => {
      if (isTouch) {
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onEnd);
      } else {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onEnd);
      }
    };

    if (isTouch) {
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onEnd);
    } else {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onEnd);
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon-inactive">↕</span>;
    }
    return sortConfig.direction === 'asc' ? (
      <span className="sort-icon-active">▲</span>
    ) : (
      <span className="sort-icon-active">▼</span>
    );
  };

  return (
    <div className="table-list-container">
      {/* Search & Header Action Toolbar */}
      <div className="table-toolbar">
        <div className="filters">
          {onSearchChange && (
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
          )}
        </div>

        {renderHeaderActions && <div className="header-actions">{renderHeaderActions()}</div>}
      </div>

      {/* Main Data Grid Table */}
      <div className="data-table-wrapper">
        {loading ? (
          <Loader label="Loading table records..." />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((col) => {
                  const isSortable = col.sortable !== false;
                  return (
                    <th
                      key={col.key}
                      style={{
                        position: 'relative',
                        cursor: isSortable && onSort ? 'pointer' : 'default',
                        userSelect: 'none',
                        width: colWidths[col.key] || col.width || 'auto',
                      }}
                      onClick={(e) => {
                        // Don't trigger sorting when user is dragging the resizer
                        if (e.target.classList.contains('column-resizer')) return;
                        isSortable && onSort && onSort(col.key);
                      }}
                    >
                      <div className="table-header-content">
                        {col.label}
                        {isSortable && onSort && getSortIcon(col.key)}
                      </div>
                      <div
                        className="column-resizer"
                        onMouseDown={(e) => startResize(e, col.key)}
                        onTouchStart={(e) => startResize(e, col.key)}
                      />
                    </th>
                  );
                })}

                {(onEdit || onDelete || renderActions) && <th className="th-actions">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedData && paginatedData.length > 0 ? (
                paginatedData.map((row, index) => {
                  const rowId = row[idKey] || index;
                  return (
                    <tr key={rowId}>
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            width: colWidths[col.key] || col.width || 'auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col.render ? col.render(row) : row[col.key] !== undefined && row[col.key] !== null ? String(row[col.key]) : '-'}
                        </td>
                      ))}

                      {(onEdit || onDelete || renderActions) && (
                        <td className="td-actions">
                          <div className="row-actions row-actions-end">
                            {renderActions && renderActions(row)}
                            {onEdit && (
                              <button
                                className="btn-table-action btn-table-action-edit"
                                onClick={() => onEdit(row)}
                                title="Edit"
                              >
                                Edit
                              </button>
                            )}
                            {onDelete && (
                              <button
                                className="btn-table-action btn-table-action-delete"
                                onClick={() => onDelete(row)}
                                title="Delete"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr className="empty-row">
                  <td colSpan={columns.length + (onEdit || onDelete || renderActions ? 1 : 0)}>
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Footer */}
      {!loading && totalRecords > 0 && onPageChange && (
        <div className="table-pagination">
          <div>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalRecords)} to{' '}
            {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
          </div>

          <div className="pagination-controls">
            {onPageSizeChange && (
              <div className="page-size-selector">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            )}

            <div className="pagination-buttons">
              <button
                className="btn btn-secondary btn-pagination"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
              >
                ◀ Prev
              </button>
              <span className="pagination-indicator">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-secondary btn-pagination"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange(currentPage + 1)}
              >
                Next ▶
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

TableList.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array,
  paginatedData: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  idKey: PropTypes.string,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  onSort: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalRecords: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  renderActions: PropTypes.func,
  renderHeaderActions: PropTypes.func,
};
